import React from 'react'
import { Bullet, Bullets, Info, Page, Section, Step } from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const DopsAndLogbook = () => (
  <Page
    title="DOPS and the Procedure Logbook"
    intro="DOPS (Direct Observation of Procedural Skills) assessments and the procedure logbook give clinicians a workplace-based assessment trail and a running count of the procedures they perform, built directly from their ePCRs."
  >
    <Section title="Who can do this?">
      <p>
        Every signed-in user has <DocLink url="/dops" text="DOPS & Logbook" />{' '}
        for their own DOPS and logbook. The organisation-wide view at{' '}
        <DocLink url="/governance/dops" text="DOPS & Logbooks" /> — all
        assessments and any clinician&apos;s logbook — is limited to the{' '}
        <strong>Governance</strong> team.
      </p>
    </Section>

    <Section title="Requesting a DOPS">
      <Step number={1} title="Open the intervention on the ePCR">
        A DOPS is requested from a completed intervention entry on an ePCR —
        including entries logged on a Critical Care live console once their
        details are filled in. Choose <strong>Request DOPS</strong> on the
        entry.
      </Step>
      <Step number={2} title="Choose an assessor">
        Pick the colleague who observed the procedure and optionally add a
        message. The assessor is notified and the request appears in their
        &quot;Requested of me&quot; list. You cannot assess your own
        procedure.
      </Step>
      <Info title="Who can request one?">
        Only someone involved in the record can request a DOPS on it, and
        where the entry names the operator, only the clinician recorded as
        performing the procedure can request one for it. The DOPS stores a
        snapshot of the clinical entry only — no patient-identifying fields —
        so assessors and Governance can read it without opening the record.
      </Info>
    </Section>

    <Section title="Completing an assessment">
      <Bullets>
        <Bullet>
          The assessor rates the standard WPBA domains — indications and
          anatomy, consent, preparation, team briefing, analgesia or
          sedation, technical ability, asepsis, recognising complications,
          post-procedure management, communication, and professionalism —
          each as Below expectations, Borderline, Meets expectations, Above
          expectations, or Not observed.
        </Bullet>
        <Bullet>
          An overall judgement is required: needs further supervised
          practice, competent with indirect supervision, or competent to
          perform independently — plus free-text strengths, development
          points, and agreed actions.
        </Bullet>
        <Bullet>
          The assessor can instead <strong>decline</strong> the request with
          a reason (for example, they did not observe the procedure), and the
          trainee can <strong>withdraw</strong> a request they raised while
          it is still waiting. The trainee is notified of the outcome either
          way.
        </Bullet>
        <Bullet>
          Once the DOPS is completed, the trainee can add a written{' '}
          <strong>reflection</strong> to it.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="The clinician's view: /dops">
      <p>
        <DocLink url="/dops" text="DOPS & Logbook" /> has three tabs:
      </p>
      <Bullets>
        <Bullet>
          <strong>Requested of me</strong> — DOPS waiting for you as the
          assessor, plus everything you previously assessed.
        </Bullet>
        <Bullet>
          <strong>My DOPS</strong> — your own assessments as the trainee, at
          every status (Requested, Completed, Declined, Withdrawn).
        </Bullet>
        <Bullet>
          <strong>My logbook</strong> — your procedure logbook (below).
        </Bullet>
      </Bullets>
    </Section>

    <Section title="The procedure logbook">
      <p>
        The logbook is counted automatically from the ePCRs you were on over a
        chosen period (3 to 36 months, 12 by default) — nothing is entered by
        hand. For each procedure it distinguishes{' '}
        <strong>performed</strong> (you are recorded as the operator) from{' '}
        <strong>on case</strong> (no operator recorded), with the last
        performed date and the individual entries behind each row.
      </p>
      <Bullets>
        <Bullet>
          Headline tiles show records reviewed, procedures performed,
          intubation first-pass success rate, and completed DOPS.
        </Bullet>
        <Bullet>
          <strong>Skill currency</strong> cards track each procedure against
          the currency targets set in the Critical Care settings card in Site
          Configuration (for example, a minimum number of intubations in a
          rolling period), marked Current or Below target.
        </Bullet>
        <Bullet>
          Completed DOPS are listed under the logbook, so assessments and
          exposure sit together as one development record.
        </Bullet>
      </Bullets>
      <Info title="Clinicians cannot open submitted records">
        Logbook entries name the ePCR number, but only Governance users can
        open the underlying record from the logbook — clinicians cannot open
        submitted records.
      </Info>
    </Section>

    <Section title="Governance oversight: /governance/dops">
      <Bullets>
        <Bullet>
          The <strong>All DOPS</strong> tab lists every assessment in the
          organisation, with stat tiles (awaiting assessor, completed,
          declined, withdrawn) and a status filter.
        </Bullet>
        <Bullet>
          The <strong>Clinician logbooks</strong> tab opens any clinician&apos;s
          procedure logbook, skill currency, and completed DOPS — the same
          view the clinician sees, with links through to the records.
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default DopsAndLogbook
